import { GraphicObjectCreator } from "../graphic-object-creator";
import { FixedDimension, PercentDimension } from "../../../../../../web-graphics";
import {
  getChannelUnitScale,
  Plot,
  FixedPointCounter,
  FreqPointCounter,
  VariableIsolineSignalLine,
  ConstIsolineSignalLine,
  HistogramSignal
} from "../../../../../../web-visualizator";

export class SignalsCreator extends GraphicObjectCreator {
  constructor() {
    super();
  }

  create(settings) {
    let calllbackArray = this.callbacks.get("beforeCreate");
    calllbackArray.forEach(callback => callback(settings, this));

    const signalType = settings.signalSettings.type ?? "line";

    const channelScaleX =
      settings.channelSettings._isSynchronizedScaleX ||
      settings.channelSettings.scaleX === undefined ||
      settings.channelSettings.scaleX.index === undefined ||
      settings.channelSettings.scaleX.values === undefined
        ? settings.signalsBoxSettings.scaleX.currValue
        : settings.channelSettings.scaleX.values[settings.channelSettings.scaleX.index];
    const channelScaleY =
      settings.channelSettings.scaleY !== undefined &&
      settings.channelSettings.scaleY.index !== undefined &&
      settings.channelSettings.scaleY.values !== undefined
        ? settings.channelSettings.scaleY.values[settings.channelSettings.scaleY.index]
        : settings.signalsBoxSettings.scaleY.currValue;
    const channelFilters = settings.channelSettings.filters ?? settings.signalsBoxSettings.selectedFiltersNames;

    const plotSignalsSettings = Plot.findContentSettings(settings.plotSettings, "signals", "central");

    let unitScale = getChannelUnitScale(settings.channelSettings, settings.commonSettings.data);
    if (unitScale === undefined) {
      unitScale = 1;
    }
    const signalLineSettings = {
      id: settings.signalSettings.id,
      type: settings.signalSettings.type,
      fillType: settings.signalSettings.fillType,
      location: {
        x: new FixedDimension(0),
        y: new FixedDimension(settings.isolineY)
      },
      size: {
        width: new PercentDimension(1),
        height: new PercentDimension(1)
      },
      pointCounterCreator: this._getPointCounterCreator(settings.signalSettings.fillType),
      color: settings.signalSettings.color,
      freq: settings.channelSettings.freq,
      scaleY: (channelScaleY * unitScale) / settings.signalsBoxSettings.defaultCellSize_mm,
      scaleX: channelScaleX,
      filters: this._selectFilters(plotSignalsSettings.filters, settings.signalsBoxSettings),
      selectedFiltersNames: channelFilters,
      hasVariableIsoline: settings.channelSettings.hasVariableIsoline,
      isolineBorder: settings.isolineSettings.updateBorder,
      viewScale: plotSignalsSettings.viewScale,
      signalSettings: settings.signalSettings,
      valuePixelConvertKoef: (settings.signalsBoxSettings.defaultCellSize_mm * settings.ppcm) / 10,
      isBuffered: settings.commonSettings?.data?.isBuffered ?? false,
      pointCount: settings.signalSettings.pointCount,
      parent: {
        ref: settings.parent
      },
      ppcm: settings.ppcm
    };

    let createdObject = undefined;
    switch (signalType) {
      case "line": {
        let fullLineType = "";
        if (settings.channelSettings.hasVariableIsoline) {
          fullLineType += "variable-isoline-";
        } else {
          fullLineType += "const-isoline-";
        }
        fullLineType += "line";
        createdObject = this._createLine(fullLineType, signalLineSettings);
        break;
      }
      case "histogram":
        createdObject = new HistogramSignal(signalLineSettings);
        break;
    }
    createdObject.init?.();

    calllbackArray = this.callbacks.get("afterCreate");
    calllbackArray.forEach(callback => callback(createdObject, settings, this));
    return createdObject;
  }

  _getPointCounterCreator(fillType) {
    switch (fillType) {
      case "period":
        return bufferObject => {
          return new FreqPointCounter({
            freq: bufferObject.settings.freq,
            width: bufferObject.settings.plotSize.width,
            ppmm: bufferObject.settings.ppmm
          });
        };
      case "custom":
        return bufferObject => {
          return new FixedPointCounter({
            pointCount: bufferObject.settings.pointCount
          });
        };
      default:
        return undefined;
    }
  }

  _createLine(fullLineType, signalLineSettings) {
    switch (fullLineType) {
      case "variable-isoline-line":
        return new VariableIsolineSignalLine(signalLineSettings);
      case "const-isoline-line":
        return new ConstIsolineSignalLine(signalLineSettings);
    }
    return undefined;
  }

  _selectFilters(filtersNames, signalsBoxSettings) {
    const selectedFilters = [];
    for (let filterIndex = 0; filterIndex < signalsBoxSettings.filters.length; filterIndex++) {
      const filterInfo = signalsBoxSettings.filters[filterIndex];
      if (filtersNames.indexOf(filterInfo.name) >= 0) {
        selectedFilters.push(filterInfo);
      }
    }
    return selectedFilters;
  }
}
